"use client";

import {
    NextIntlClientProvider,
    IntlErrorCode,
    type IntlError,
} from "next-intl";

/**
 * Client-side wrapper around NextIntlClientProvider that re-declares the
 * dev-loud `onError` and `getMessageFallback` handlers from server config.
 *
 * Why this exists:
 *   `src/i18n/request.ts` (server-side `getRequestConfig`) wires both
 *   handlers, but those are functions — they don't serialize across the
 *   RSC boundary, so client components fall back to next-intl's default
 *   (render the raw dotted key as the value, no log). That's how the
 *   recent translation-key regression slipped past — the server logged
 *   loudly, but the client just rendered `common.commentTab` literally
 *   in the UI without any visible warning.
 *
 *   Here we re-attach the same handlers on the CLIENT provider so missing
 *   keys also fail loudly there: dev console gets a `[i18n] MISSING_MESSAGE`
 *   error, and the rendered fallback becomes `⟨MISSING:namespace.key⟩` —
 *   visually unmissable instead of a key that looks almost like real text.
 *
 *   In production both branches return empty so `t("...") || "Default"`
 *   patterns keep working as the operator-facing safety net.
 *
 * Why `locale` and `messages` are passed in:
 *   When NextIntlClientProvider is rendered directly in the server tree
 *   (e.g. inside a server-component layout), it auto-inherits locale +
 *   messages from `getRequestConfig`. But once we wrap it in our own
 *   client component, the server context can't reach the provider — so
 *   we forward locale and messages explicitly from the root layout's
 *   `getLocale()` / `getMessages()` calls.
 */
interface I18nClientProviderProps {
    children: React.ReactNode;
    locale: string;
    messages: Record<string, unknown>;
}

export function I18nClientProvider({
    children,
    locale,
    messages,
}: I18nClientProviderProps) {
    return (
        <NextIntlClientProvider
            locale={locale}
            messages={messages}
            onError={(error: IntlError) => {
                if (
                    process.env.NODE_ENV !== "production" &&
                    error.code === IntlErrorCode.MISSING_MESSAGE
                ) {
                    // eslint-disable-next-line no-console
                    console.error(
                        "[i18n] MISSING_MESSAGE:",
                        error.message,
                        error.originalMessage,
                    );
                    return;
                }
                // Other error classes (formatting, invalid message) keep
                // the library default of console.error so they're not
                // silently swallowed in either environment.
                // eslint-disable-next-line no-console
                console.error(error);
            }}
            getMessageFallback={({ namespace, key, error }) => {
                if (
                    process.env.NODE_ENV !== "production" &&
                    error.code === IntlErrorCode.MISSING_MESSAGE
                ) {
                    return `⟨MISSING:${namespace ? namespace + "." : ""}${key}⟩`;
                }
                // Production: empty string keeps the existing
                // `t("...") || "Default"` consumer pattern functional.
                return "";
            }}
        >
            {children}
        </NextIntlClientProvider>
    );
}
